import React, { useState, useRef, useEffect } from 'react';
import { FiMoreVertical, FiPlus, FiEdit2, FiTrash2, FiCopy, FiSettings, FiList, FiLink } from 'react-icons/fi';
import StatusDropdown from './StatusDropdown';
import PriorityDropdown from './PriorityDropdown';
import AssigneePicker from './AssigneePicker';
import AddItemModal from './AddItemModal';
import AddGroupModal from './AddGroupModal';
import GroupSettingsModal from './GroupSettingsModal';
import EditTaskModal from './EditTaskModal';
import MobileBoardTaskList from './MobileBoardTaskList';
import './BoardTable.css';

const useIsMobile = (bp = 768) => {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const fn = () => setM(window.innerWidth < bp);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [bp]);
  return m;
};

const BoardTable = ({ board, token, user, onUpdate }) => {
  const [editingCell, setEditingCell] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editTaskInitialTab, setEditTaskInitialTab] = useState('details');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [groupMenuOpen, setGroupMenuOpen] = useState(null);
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [paymentsByItem, setPaymentsByItem] = useState({});
  const actionMenuRef = useRef(null);
  const groupMenuRef = useRef(null);
  const isMobile = useIsMobile();

  // Fetch finance summary on mobile to show cost/paid/remaining per task
  useEffect(() => {
    if (!isMobile || !board?.id) return;
    const fetchFin = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards/${board.id}/finance-summary`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const map = {};
          (data.items || []).forEach((it) => { map[it.itemId] = { paid: it.paid || 0, remaining: it.remaining || 0 }; });
          setPaymentsByItem(map);
        }
      } catch (_e) { /* ignore */ }
    };
    fetchFin();
  }, [isMobile, board?.id, board?.groups, token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActionMenuOpen(null);
      }
      if (groupMenuRef.current && !groupMenuRef.current.contains(event.target)) {
        setGroupMenuOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getColorForGroup = (index) => {
    const colors = ['#4F8DFF', '#9B6BFF', '#1DBF73', '#F4A340', '#E24B5B', '#66ccff'];
    return colors[index % colors.length];
  };

  const handleCellEdit = async (itemId, field, value) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/items/${itemId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: value })
      });
      if (response.ok) {
        onUpdate();
        setEditingCell(null);
      }
    } catch (error) {
      console.error('Failed to update item:', error);
    }
  };

  const handleGroupNameEdit = async (groupId, newName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups/${groupId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newName })
      });
      if (response.ok) {
        onUpdate();
        setEditingCell(null);
      }
    } catch (error) {
      console.error('Failed to update group:', error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/items/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        onUpdate();
        setActionMenuOpen(null);
      }
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
  };

  const handleDuplicateItem = async (itemId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/items/${itemId}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        onUpdate();
        setActionMenuOpen(null);
      }
    } catch (error) {
      console.error('Failed to duplicate item:', error);
    }
  };

  const handleDuplicateGroup = async (groupId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups/${groupId}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        onUpdate();
        setGroupMenuOpen(null);
      }
    } catch (error) {
      console.error('Failed to duplicate group:', error);
    }
  };

  const handleAddItem = () => {
    onUpdate();
  };

  const handleAddGroup = () => {
    onUpdate();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  const sortItems = (items) => {
    if (!sortField) return items;
    
    return [...items].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'dueDate' || sortField === 'startDate') {
        aVal = aVal ? new Date(aVal) : new Date('9999-12-31');
        bVal = bVal ? new Date(bVal) : new Date('9999-12-31');
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <div className="board-table-container">
      <div className="board-table-header">
        <button 
          className="btn btn-primary btn-sm"
          onClick={() => setShowGroupModal(true)}
          data-testid="add-group-button"
        >
          <FiPlus size={16} />
          <span>Add Group</span>
        </button>
      </div>
      
      <div className="board-table">
        {(!board.groups || board.groups.length === 0) ? (
          <div className="empty-state" style={{ padding: 'var(--space-10)', textAlign: 'center' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>No Groups Yet</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>Create your first group to start organizing tasks</p>
            <button 
              className="btn btn-primary"
              onClick={() => setShowGroupModal(true)}
              data-testid="empty-state-add-group"
            >
              <FiPlus size={16} />
              <span>Add First Group</span>
            </button>
          </div>
        ) : (
          <>
            {(board.groups || []).map((group, groupIndex) => (
          <div key={group.id} className="table-group">
            <div
              className="group-header"
              style={{ backgroundColor: group.color || getColorForGroup(groupIndex) }}
            >
              <div className="group-header-left">
                {editingCell === `group-${group.id}` ? (
                  <input
                    type="text"
                    defaultValue={group.name}
                    autoFocus
                    onBlur={(e) => handleGroupNameEdit(group.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleGroupNameEdit(group.id, e.target.value);
                      }
                    }}
                    className="group-name-input"
                    data-testid="group-name-input"
                  />
                ) : (
                  <span 
                    className="group-name" 
                    onClick={() => setEditingCell(`group-${group.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    {group.name}
                  </span>
                )}
                <span className="group-count">{sortItems(group.items || []).length} items</span>
              </div>
              <div style={{ position: 'relative' }} ref={groupMenuOpen === group.id ? groupMenuRef : null}>
                <button 
                  className="group-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGroupMenuOpen(groupMenuOpen === group.id ? null : group.id);
                  }}
                  data-testid="group-action-button"
                >
                  <FiMoreVertical size={16} />
                </button>
                {groupMenuOpen === group.id && (
                  <div className="dropdown" style={{ right: 0, top: '100%', marginTop: '4px' }}>
                    <div 
                      className="dropdown-item" 
                      onClick={() => {
                        setSelectedGroup(group);
                        setShowGroupSettings(true);
                        setGroupMenuOpen(null);
                      }}
                    >
                      <FiSettings size={14} />
                      <span>Group Settings</span>
                    </div>
                    <div className="dropdown-item" onClick={() => setEditingCell(`group-${group.id}`)}>
                      <FiEdit2 size={14} />
                      <span>Rename Group</span>
                    </div>
                    <div 
                      className="dropdown-item" 
                      onClick={() => {
                        handleDuplicateGroup(group.id);
                        setGroupMenuOpen(null);
                      }}
                    >
                      <FiCopy size={14} />
                      <span>Duplicate Group</span>
                    </div>
                    <div className="dropdown-divider"></div>
                    <div 
                      className="dropdown-item" 
                      onClick={async () => {
                        if (!window.confirm(`Delete group "${group.name}"? All tasks will be deleted.`)) return;
                        try {
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups/${group.id}`, {
                            method: 'DELETE',
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (response.ok) {
                            onUpdate();
                            setGroupMenuOpen(null);
                          }
                        } catch (error) {
                          console.error('Failed to delete group:', error);
                        }
                      }}
                      style={{ color: '#E24B5B' }}
                    >
                      <FiTrash2 size={14} />
                      <span>Delete Group</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {!isMobile && (
            <>
            <div className="table-header">
              <div className="table-cell task-name-cell" onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                Task {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </div>
              <div className="table-cell status-cell" onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
              </div>
              <div className="table-cell priority-cell" onClick={() => handleSort('priority')} style={{ cursor: 'pointer' }}>
                Priority {sortField === 'priority' && (sortDirection === 'asc' ? '↑' : '↓')}
              </div>
              <div className="table-cell assignee-cell">Assignee</div>
              <div className="table-cell date-cell" onClick={() => handleSort('startDate')} style={{ cursor: 'pointer' }}>
                Start Date {sortField === 'startDate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </div>
              <div className="table-cell date-cell" onClick={() => handleSort('dueDate')} style={{ cursor: 'pointer' }}>
                Due Date {sortField === 'dueDate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </div>
              <div className="table-cell progress-cell" onClick={() => handleSort('progress')} style={{ cursor: 'pointer' }}>
                Progress {sortField === 'progress' && (sortDirection === 'asc' ? '↑' : '↓')}
              </div>
              <div className="table-cell actions-cell"></div>
            </div>

            {sortItems(group.items || []).map((item) => (
              <div key={item.id} className="table-row" data-testid="board-table-row">
                <div className="table-cell task-name-cell">
                  {editingCell === `${item.id}-name` ? (
                    <input
                      type="text"
                      defaultValue={item.name}
                      autoFocus
                      onBlur={(e) => handleCellEdit(item.id, 'name', e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleCellEdit(item.id, 'name', e.target.value);
                        }
                      }}
                      className="cell-input"
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {item.dependencies && item.dependencies.length > 0 && (
                        <span title={`Has ${item.dependencies.length} dependenc${item.dependencies.length === 1 ? 'y' : 'ies'}`}>
                          <FiLink 
                            size={14} 
                            style={{ color: 'var(--monday-blue)', flexShrink: 0 }} 
                          />
                        </span>
                      )}
                      <span
                        onClick={() => {
                          setSelectedTask(item);
                          setEditTaskInitialTab('details');
                          setShowEditTaskModal(true);
                        }}
                        className="cell-editable"
                        style={{ cursor: 'pointer' }}
                      >
                        {item.name}
                      </span>
                      {item.subtasks && item.subtasks.length > 0 && (() => {
                        const done = item.subtasks.filter((s) => s.completed || s.status === 'Done').length;
                        return (
                          <span
                            title={`${done}/${item.subtasks.length} subtasks completed`}
                            className="subtask-badge"
                            onClick={() => {
                              setSelectedTask(item);
                              setEditTaskInitialTab('details');
                              setShowEditTaskModal(true);
                            }}
                            style={{ cursor: 'pointer', fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--gray-100)', color: 'var(--text-secondary)', fontWeight: 600, marginLeft: 4, whiteSpace: 'nowrap' }}
                          >
                            ☑ {done}/{item.subtasks.length}
                          </span>
                        );
                      })()}
                    </div>
                  )}
                </div>
                <div className="table-cell status-cell">
                  <StatusDropdown
                    value={item.status}
                    onChange={(newStatus) => handleCellEdit(item.id, 'status', newStatus)}
                  />
                </div>
                <div className="table-cell priority-cell">
                  <PriorityDropdown
                    value={item.priority}
                    onChange={(newPriority) => handleCellEdit(item.id, 'priority', newPriority)}
                  />
                </div>
                <div className="table-cell assignee-cell">
                  <AssigneePicker
                    assignees={item.assignees || []}
                    onChange={(newAssignees) => handleCellEdit(item.id, 'assignees', newAssignees)}
                    token={token}
                  />
                </div>
                <div className="table-cell date-cell">
                  {editingCell === `${item.id}-startDate` ? (
                    <input
                      type="date"
                      defaultValue={formatDateForInput(item.startDate)}
                      autoFocus
                      onBlur={(e) => {
                        if (e.target.value) {
                          handleCellEdit(item.id, 'startDate', new Date(e.target.value).toISOString());
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value) {
                          handleCellEdit(item.id, 'startDate', new Date(e.target.value).toISOString());
                        }
                      }}
                      className="cell-input"
                    />
                  ) : (
                    <span 
                      className="cell-date cell-editable" 
                      onClick={() => setEditingCell(`${item.id}-startDate`)}
                    >
                      {formatDate(item.startDate)}
                    </span>
                  )}
                </div>
                <div className="table-cell date-cell">
                  {editingCell === `${item.id}-dueDate` ? (
                    <input
                      type="date"
                      defaultValue={formatDateForInput(item.dueDate)}
                      autoFocus
                      onBlur={(e) => {
                        if (e.target.value) {
                          handleCellEdit(item.id, 'dueDate', new Date(e.target.value).toISOString());
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value) {
                          handleCellEdit(item.id, 'dueDate', new Date(e.target.value).toISOString());
                        }
                      }}
                      className="cell-input"
                    />
                  ) : (
                    <span 
                      className="cell-date cell-editable" 
                      onClick={() => setEditingCell(`${item.id}-dueDate`)}
                    >
                      {formatDate(item.dueDate)}
                    </span>
                  )}
                </div>
                <div className="table-cell progress-cell">
                  {editingCell === `${item.id}-progress` ? (
                    <input
                      type="number"
                      min="0"
                      max="100"
                      defaultValue={item.progress || 0}
                      autoFocus
                      onBlur={(e) => handleCellEdit(item.id, 'progress', parseInt(e.target.value) || 0)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleCellEdit(item.id, 'progress', parseInt(e.target.value) || 0);
                        }
                      }}
                      className="cell-input"
                      style={{ width: '60px' }}
                    />
                  ) : (
                    <div 
                      className="progress-bar" 
                      onClick={() => setEditingCell(`${item.id}-progress`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div
                        className="progress-fill"
                        style={{ width: `${item.progress || 0}%` }}
                      ></div>
                      <span className="progress-text">{item.progress || 0}%</span>
                    </div>
                  )}
                </div>
                <div className="table-cell actions-cell">
                  <div style={{ position: 'relative' }} ref={actionMenuOpen === item.id ? actionMenuRef : null}>
                    <button 
                      className="row-action-btn" 
                      onClick={() => setActionMenuOpen(actionMenuOpen === item.id ? null : item.id)}
                      data-testid="row-action-button"
                    >
                      <FiMoreVertical size={16} />
                    </button>
                    {actionMenuOpen === item.id && (
                      <div className="dropdown" style={{ right: 0, top: '100%', marginTop: '4px' }}>
                        <div 
                          className="dropdown-item" 
                          onClick={() => {
                            setSelectedTask(item);
                            setShowEditTaskModal(true);
                            setActionMenuOpen(null);
                          }}
                        >
                          <FiEdit2 size={14} />
                          <span>Edit Details</span>
                        </div>
                        <div 
                          className="dropdown-item" 
                          onClick={() => {
                            handleDuplicateItem(item.id);
                            setActionMenuOpen(null);
                          }}
                        >
                          <FiCopy size={14} />
                          <span>Duplicate</span>
                        </div>
                        <div 
                          className="dropdown-item"
                          onClick={() => {
                            setSelectedTask(item);
                            setShowEditTaskModal(true);
                            setActionMenuOpen(null);
                          }}
                        >
                          <FiList size={14} />
                          <span>Manage Subtasks</span>
                        </div>
                        <div className="dropdown-divider"></div>
                        <div className="dropdown-item" onClick={() => handleDeleteItem(item.id)} style={{ color: '#E24B5B' }}>
                          <FiTrash2 size={14} />
                          <span>Delete</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            </>
            )}

            {isMobile && (
              <MobileBoardTaskList
                group={group}
                token={token}
                boardBaseCurrency={board?.baseCurrency || 'USD'}
                paymentsByItem={paymentsByItem}
                onUpdate={onUpdate}
                onDelete={(item) => handleDeleteItem(item.id)}
                onEdit={(item, initialTab) => {
                  setSelectedTask(item);
                  setEditTaskInitialTab(initialTab === 'finance' ? 'finance' : 'details');
                  setShowEditTaskModal(true);
                }}
              />
            )}

            <div className="table-row add-item-row">
              <button 
                className="add-item-btn" 
                onClick={() => {
                  setSelectedGroupId(group.id);
                  setShowItemModal(true);
                }}
                data-testid="add-item-button"
              >
                <FiPlus size={16} />
                <span>Add Item</span>
              </button>
            </div>
          </div>
        ))}
          </>
        )}
      </div>
      
      <AddItemModal
        isOpen={showItemModal}
        onClose={() => setShowItemModal(false)}
        onAdd={handleAddItem}
        groupId={selectedGroupId}
        token={token}
        boardBaseCurrency={board?.baseCurrency || 'USD'}
      />
      
      <AddGroupModal
        isOpen={showGroupModal}
        onClose={() => setShowGroupModal(false)}
        boardId={board.id}
        token={token}
        onAdd={handleAddGroup}
      />
      
      <GroupSettingsModal
        isOpen={showGroupSettings}
        onClose={() => setShowGroupSettings(false)}
        group={selectedGroup}
        token={token}
        onUpdate={onUpdate}
      />
      
      <EditTaskModal
        isOpen={showEditTaskModal}
        onClose={() => { setShowEditTaskModal(false); setEditTaskInitialTab('details'); }}
        task={selectedTask}
        token={token}
        boardId={board.id}
        boardBaseCurrency={board?.baseCurrency || 'USD'}
        initialTab={editTaskInitialTab}
        onUpdate={onUpdate}
      />
    </div>
  );
};

export default BoardTable;